/**
 * 文件URL统一处理工具
 * 提供统一的文件URL获取逻辑，支持本地文件、OSS文件、外部链接等
 */

import { getMediaUrl } from '../config';

/**
 * 获取统一的文件URL
 * 
 * 优先级：
 * 1. OSS文件对象 (oss_file.full_url)
 * 2. OSS文件路径 (oss_file_path)
 * 3. 外部链接 (file_url)
 * 4. 本地文件 (file)
 * 
 * @param {Object} document - 文档对象
 * @returns {string} 完整的文件URL
 */
export const getUnifiedFileUrl = (document) => {
  if (!document) {
    return '';
  }

  try {
    // 1. 优先使用OSS文件对象
    if (document.oss_file && document.oss_file.full_url) {
      const url = normalizeOssUrl(document.oss_file.full_url);
      console.debug('使用OSS文件对象URL:', url);
      return url;
    }

    // 2. 使用OSS文件路径
    if (document.oss_file_path) {
      const url = buildOssUrl(document.oss_file_path);
      if (url) {
        console.debug('使用OSS文件路径URL:', url);
        return url;
      }
    }

    // 3. 使用外部链接
    if (document.file_url) {
      const url = normalizeOssUrl(document.file_url);
      console.debug('使用外部链接URL:', url);
      return url;
    }

    // 4. 使用本地文件
    if (document.file) {
      const url = buildLocalFileUrl(document.file);
      if (url) {
        console.debug('使用本地文件URL:', url);
        return url;
      }
    }

    console.warn('文档没有可用的文件URL:', document.id);
    return '';
  } catch (error) {
    console.error('获取文件URL失败:', error);
    return '';
  }
};

/**
 * 获取文件信息
 * 
 * @param {Object} document - 文档对象
 * @returns {Object} 包含文件信息的对象
 */
export const getFileInfo = (document) => {
  if (!document) {
    return {};
  }

  const fileUrl = getUnifiedFileUrl(document);

  const info = {
    fileUrl,
    fileType: 'unknown',
    fileSize: 0,
    isExternal: false,
    isOss: false,
    isLocal: false,
    isGoogleDocs: false,
    isSameOrigin: false
  };

  if (!fileUrl) {
    return info;
  }

  // 判断文件类型
  if (fileUrl.startsWith('http')) {
    if (fileUrl.includes('oss') || fileUrl.includes('aliyuncs.com')) {
      info.isOss = true;
      info.fileType = 'oss';
    } else if (isGoogleDocsDomain(fileUrl)) {
      info.isExternal = true;
      info.isGoogleDocs = true;
      info.fileType = 'google_docs';
    } else {
      info.isExternal = true;
      info.fileType = 'external';
    }
  } else {
    info.isLocal = true;
    info.fileType = 'local';
  }


  // 检查是否同源
  info.isSameOrigin = isSameOrigin(fileUrl);

  // 获取文件大小（如果可能）
  try {
    if (document.oss_file && document.oss_file.file_size) {
      info.fileSize = document.oss_file.file_size;
    } else if (document.file && document.file.size) {
      info.fileSize = document.file.size;
    }
  } catch (error) {
    console.debug('获取文件大小失败:', error);
  }

  return info;
};

/**
 * 获取下载URL（与预览URL相同，但可以添加下载参数）
 * 
 * @param {Object} document - 文档对象
 * @returns {string} 下载URL
 */
export const getDownloadUrl = (document) => {
  let fileUrl = getUnifiedFileUrl(document);

  if (!fileUrl) {
    return '';
  }

  // 对于Google Drive链接，添加下载参数
  if (fileUrl.includes('drive.google.com')) {
    if (!fileUrl.includes('export=download')) {
      if (fileUrl.includes('?')) {
        fileUrl += '&export=download';
      } else {
        fileUrl += '?export=download';
      }
    }
  }

  return fileUrl;
};

/**
 * 获取预览URL（与文件URL相同）
 * 
 * @param {Object} document - 文档对象
 * @returns {string} 预览URL
 */
export const getPreviewUrl = (document) => {
  return getUnifiedFileUrl(document);
};

/**
 * 确保URL使用HTTPS协议
 * 
 * @param {string} url - 原始URL
 * @returns {string} HTTPS URL
 */
const OSS_FALLBACK_HOST = 'blooart-yunkong-files.oss-cn-hangzhou.aliyuncs.com';

const ensureHttpsUrl = (url) => {
  if (!url) {
    return '';
  }

  if (url.startsWith('http://')) {
    return url.replace('http://', 'https://', 1);
  } else if (!url.startsWith('https://')) {
    return `https://${url}`;
  }

  return url;
};

/**
 * 自定义 OSS 域名证书异常时，回退到 OSS 原生域名
 */
const normalizeOssUrl = (url) => {
  if (!url) {
    return '';
  }

  const httpsUrl = ensureHttpsUrl(url);
  if (/oss\.blooart\.cn/i.test(httpsUrl)) {
    try {
      const pathname = new URL(httpsUrl).pathname.replace(/^\//, '');
      if (pathname) {
        return `https://${OSS_FALLBACK_HOST}/${pathname}`;
      }
    } catch (error) {
      console.debug('OSS URL 回退失败:', error);
    }
  }

  return httpsUrl;
};

/**
 * 构建OSS文件URL
 * 
 * @param {string} filePath - OSS文件路径
 * @returns {string} OSS文件URL
 */
const buildOssUrl = (filePath) => {
  if (!filePath) {
    return '';
  }

  // 如果已经是完整URL，直接返回
  if (filePath.startsWith('http')) {
    return ensureHttpsUrl(filePath);
  }

  // 使用默认的 OSS 域名
  return `https://${OSS_FALLBACK_HOST}/${filePath}`;
};

/**
 * 构建本地文件URL
 * 
 * @param {string|Object} fileField - 文件字段
 * @returns {string} 本地文件URL
 */
const buildLocalFileUrl = (fileField) => {
  if (!fileField) {
    return '';
  }

  try {
    // 如果是字符串，直接使用getMediaUrl处理
    if (typeof fileField === 'string') {
      return getMediaUrl(fileField);
    }

    // 如果是对象，尝试获取URL
    if (typeof fileField === 'object') {
      if (fileField.url) {
        return fileField.url;
      }
      if (fileField.name) {
        return getMediaUrl(fileField.name);
      }
    }

    return String(fileField);
  } catch (error) {
    console.debug('构建本地文件URL失败:', error);
    return '';
  }
};

/**
 * 检查是否为Google Docs域名
 * 
 * @param {string} url - URL
 * @returns {boolean} 是否为Google Docs域名
 */
const isGoogleDocsDomain = (url) => {
  return /docs\.google\.com|drive\.google\.com/i.test(url || '');
};

/**
 * 检查是否为同源URL
 * 
 * @param {string} url - URL
 * @returns {boolean} 是否为同源
 */
const isSameOrigin = (url) => {
  try {
    const u = new URL(url, window.location.origin);
    return u.origin === window.location.origin;
  } catch (e) {
    return true;
  }
};

/**
 * 检查是否为HTTP URL
 * 
 * @param {string} url - URL
 * @returns {boolean} 是否为HTTP URL
 */
export const isHttpUrl = (url) => {
  return /^https?:\/\//i.test(url || '');
};

/**
 * 获取文件扩展名
 * 
 * @param {string} url - 文件URL
 * @param {Object} document - 文档对象（可选，用于从数据库字段获取扩展名）
 * @returns {string} 文件扩展名
 */
export const getFileExtension = (url, document = null) => {
  // 优先使用数据库中的 file_extension 字段
  if (document && document.file_extension) {
    // 返回不带点的扩展名，与预览组件期望的格式一致
    const ext = document.file_extension.toLowerCase();
    return ext.startsWith('.') ? ext.substring(1) : ext;
  }

  // 其次使用 file_type_display 字段映射
  if (document && document.file_type_display) {
    const extFromType = getFileExtensionFromType(document);
    if (extFromType) {
      // 返回不带点的扩展名
      return extFromType.startsWith('.') ? extFromType.substring(1) : extFromType;
    }
  }

  // 如果没有URL，返回空字符串
  if (!url) {
    return '';
  }

  let ext = '';

  try {
    const pathname = new URL(url).pathname;
    ext = pathname.split('.').pop().toLowerCase();
  } catch (e) {
    // 如果URL解析失败，尝试从字符串中提取
    const cleanUrl = url.split('?')[0].split('#')[0];
    ext = cleanUrl.split('.').pop().toLowerCase();
  }

  // 如果从URL无法获取扩展名，尝试从文档对象中获取
  if (!ext && document) {
    // 尝试从文件名获取
    if (document.file && typeof document.file === 'string') {
      ext = document.file.split('.').pop().toLowerCase();
    } else if (document.file && document.file.name) {
      ext = document.file.name.split('.').pop().toLowerCase();
    } else if (document.title) {
      ext = document.title.split('.').pop().toLowerCase();
    }
  }

  return ext || '';
};

/**
 * 根据数据库的file_type_display字段获取文件扩展名
 * 
 * @param {Object} document - 文档对象
 * @returns {string} 文件扩展名
 */
export const getFileExtensionFromType = (document) => {
  if (!document || !document.file_type_display) {
    return '';
  }

  const typeMap = {
    'Word文档': '.docx',
    'PDF文档': '.pdf',
    '文本文档': '.txt',
    'Markdown文档': '.md',
    '图片文件': '.jpg',
    '视频文件': '.mp4',
    '音频文件': '.mp3',
    'Excel表格': '.xlsx',
    'CSV表格': '.csv',
    'PowerPoint演示': '.pptx',
    '其他文件': ''
  };

  return typeMap[document.file_type_display] || '';
};

/**
 * 判断文件类型是否支持预览
 * 
 * @param {string} extension - 文件扩展名
 * @returns {boolean} 是否支持预览
 */
export const isPreviewable = (extension) => {
  const previewableExtensions = [
    'pdf', 'docx', 'xlsx', 'xls', 'txt', 'md', 'markdown',
    'png', 'jpg', 'jpeg', 'gif', 'bmp', 'webp', 'svg',
    'mp4', 'avi', 'mov', 'wmv', 'flv', 'webm', 'mkv',
    'json', 'xml', 'csv', 'html', 'htm'
  ];
  
  return previewableExtensions.includes(extension.toLowerCase());
};

/**
 * 判断文件类型是否支持缩放
 * 
 * @param {string} extension - 文件扩展名
 * @returns {boolean} 是否支持缩放
 */
export const isZoomable = (extension) => {
  const zoomableExtensions = [
    'docx', 'xlsx', 'xls', 'pdf', 'png', 'jpg', 'jpeg', 
    'gif', 'bmp', 'webp', 'md', 'markdown'
  ];
  
  return zoomableExtensions.includes(extension.toLowerCase());
};
